import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Typography,
} from '@mui/material';
import { PageHeader } from '../../components/shared';
import {
  AssessmentOutlined as AssessmentIcon,
  EmojiEventsOutlined as EventsIcon,
  GradeOutlined as GradeIcon,
  TableChartOutlined as TableIcon,
  FactCheckOutlined as InspectionIcon,
  PersonSearchOutlined as PersonSearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '../../context/auth-context';

/**
 * `perm` es el permiso que el SERVIDOR exige para esa página. Sin él la tarjeta no se
 * pinta: `reports.view` basta para entrar en este catálogo, pero dos de los reportes
 * exigen además `reports.view-all-zones` (`routes/reports.js` → `allZones`), así que
 * al Secretario de Zona se le ofrecían dos tarjetas que solo llevaban a un 403.
 */
const reports = [
  {
    title: 'Detalles de Calificaciones por Rúbricas',
    description: 'Ver calificaciones detalladas de cada rúbrica por evento',
    icon: <GradeIcon sx={{ fontSize: 60 }} />,
    path: '/reports/rubrics-grades-details',
    color: 'primary',
  },
  {
    title: 'Puntuación General',
    description: 'Resumen de puntuaciones generales por club y evento',
    icon: <AssessmentIcon sx={{ fontSize: 60 }} />,
    path: '/reports/notas-generales',
    color: 'secondary',
  },
  {
    title: 'Certificados de Calificaciones Generales',
    description: 'Generar certificados con todas las calificaciones',
    icon: <TableIcon sx={{ fontSize: 60 }} />,
    path: '/reports/general-grades-certificates',
    color: 'success',
  },
  {
    title: 'Resumen por Categorías de Evento',
    description: 'Calificaciones agrupadas por categorías de evento',
    icon: <EventsIcon sx={{ fontSize: 60 }} />,
    path: '/reports/grades-by-categories',
    color: 'warning',
  },
  {
    title: 'Evento Inspección',
    description: 'Calificaciones de inspección por eventos seleccionados',
    icon: <InspectionIcon sx={{ fontSize: 60 }} />,
    path: '/reports/event-inspections',
    color: 'info',
  },
  {
    title: 'Evento calificados por jueces',
    description: 'Listado de eventos calificados por jueces',
    icon: <InspectionIcon sx={{ fontSize: 60 }} />,
    path: '/reports/grades-confirmation-by-judges',
    color: 'info',
  },
  {
    title: 'Calificaciones por Evento',
    description: 'Ver calificaciones agregadas por club con detalle de rúbricas',
    icon: <GradeIcon sx={{ fontSize: 60 }} />,
    path: '/reports/grades-by-events',
    color: 'success',
  },
  {
    title: 'Ranking por Campamento',
    description: 'Ver ranking de clubes basado en calificaciones agregadas por evento',
    icon: <GradeIcon sx={{ fontSize: 60 }} />,
    path: '/reports/ranking',
    color: 'success',
  },
  {
    title: 'Calificaciones Pendientes',
    description: 'Tracking de clubes y rúbricas pendientes de calificación por evento',
    icon: <GradeIcon sx={{ fontSize: 60 }} />,
    path: '/reports/pending-grades',
    color: 'error',
  },
  {
    title: 'Rendimiento de Jueces',
    description: 'Análisis ponderado de jueces: calificaciones, estrellas, sentiment AI y velocidad de sync',
    icon: <PersonSearchIcon sx={{ fontSize: 60 }} />,
    path: '/reports/judge-performance',
    color: 'primary',
    perm: 'reports.view-all-zones',
  },
  {
    title: 'Performance de Eventos',
    description: 'Dashboard de promedios por evento, rúbrica y club. Recalculable manualmente.',
    icon: <AssessmentIcon sx={{ fontSize: 60 }} />,
    path: '/reports/event-performance',
    color: 'secondary',
    perm: 'reports.view-all-zones',
  },
];

const ReportsIndex = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthContext();
  const visibles = reports.filter(r => !r.perm || hasPermission(r.perm));

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <PageHeader title="Reportes y Estadísticas" />

      <Grid container spacing={3}>
        {visibles.map((report) => (
          <Grid item xs={12} sm={6} md={3} key={report.path}>
            <Card sx={{ height: '100%' }}>
              <CardActionArea
                onClick={() => navigate(report.path)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    textAlign="center"
                  >
                    <Box sx={{ color: `${report.color}.main`, mb: 2 }}>
                      {report.icon}
                    </Box>
                    <Typography variant="h6" component="div" gutterBottom>
                      {report.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {report.description}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default ReportsIndex;
export { default as EventInspectionsPage } from './event-inspections';
export { GradesConfirmationByJudges } from './grades-confirmation-by-judges';
export { default as GradesByEvents } from './grades-by-events';
export { default as PendingGradesReport } from './pending-grades';
